import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { FREQUENCY_OPTIONS, DISMISS_OPTIONS, normalizePromo } from "@/lib/appPromo";

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <p className="text-xs uppercase tracking-widest text-gray-400 font-bold">{label}</p>
    {children}
  </div>
);

const Picker = ({ value, onChange, options, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="bg-black/40 border-purple-500/30 text-white" data-testid={testid}>
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {options.map((o) => (
        <SelectItem key={o.value} value={o.value} data-testid={`${testid}-${o.value}`}>{o.label}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

// Admin App Promotion Controls — governs the mobile Get the App banner only.
const AppPromotionControlsCard = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/platform/app-promo")
      .then((r) => setForm(normalizePromo(r.data)))
      .catch(() => setForm(normalizePromo(null)));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/app-promo", form);
      setForm(normalizePromo(r.data));
      toast.success("App promotion settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6" style={{ background: "rgba(34, 24, 50, 0.35)", borderColor: "rgba(251, 191, 36, 0.25)" }}
      data-testid="app-promo-controls-card">
      <div className="flex items-start gap-3 mb-5">
        <img src="/rgm-play-icon.png" alt="" className="w-10 h-10 rounded-lg" />
        <div>
          <h3 className="text-xl font-bold text-white">App Promotion Controls</h3>
          <p className="text-sm text-gray-400">Mobile Get the App banner. The Get the App menu link always stays available.</p>
        </div>
      </div>
      {!form ? (
        <p className="text-sm text-gray-400" data-testid="app-promo-loading">Loading…</p>
      ) : (
        <div className="space-y-5">
          <div className="flex items-center justify-between gap-4 p-3 rounded-lg bg-black/30 border border-white/5">
            <div>
              <p className="text-white font-semibold text-sm">Promotional banner</p>
              <p className="text-xs text-gray-400" data-testid="app-promo-enabled-label">
                {form.banner_enabled ? "ON — shown on phones" : "OFF — banner hidden"}
              </p>
            </div>
            <Switch checked={form.banner_enabled}
              onCheckedChange={(v) => setForm((f) => ({ ...f, banner_enabled: v }))}
              data-testid="app-promo-enabled-switch" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Display frequency">
              <Picker value={form.frequency} options={FREQUENCY_OPTIONS} testid="app-promo-frequency"
                onChange={(v) => setForm((f) => ({ ...f, frequency: v }))} />
            </Field>
            <Field label="Dismissal duration">
              <Picker value={form.dismiss_duration} options={DISMISS_OPTIONS} testid="app-promo-dismiss"
                onChange={(v) => setForm((f) => ({ ...f, dismiss_duration: v }))} />
            </Field>
          </div>
          <p className="text-xs text-gray-500">
            Never shown inside live rooms or the studio. No new pop-ups on contest pages or while music is playing.
          </p>
          <Button onClick={save} disabled={saving}
            className="bg-amber-400 hover:bg-amber-300 text-black font-bold"
            data-testid="app-promo-save">
            {saving ? "Saving…" : "Save settings"}
          </Button>
        </div>
      )}
    </Card>
  );
};

export default AppPromotionControlsCard;
